import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StoreService } from 'src/app/services/store.service';
import { MaterialModule } from 'src/app/shared/material.module';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.vfs;

@Component({
  selector: 'app-material-request-print',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MatProgressBar],
  templateUrl: './material-request-print.component.html',
  styleUrl: './material-request-print.component.scss'
})
export class MaterialRequestPrintComponent implements OnInit, OnDestroy {
  requestId!: number;
  requestData: any;
  previewData: any = null;
  isDialogMode = false;
  includeSignature = false;
  pdfDoc: any;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private storeService: StoreService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<MaterialRequestPrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.requestId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.requestId) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
  }

  printPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.download(`Material-Request-${this.previewData?.requestCode || this.requestId}.pdf`);
  }

  async sendMail() {
    this.snackBar.open('Material request mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadData(): void {
    this.storeService.getMaterialRequestPrintById(this.requestId).subscribe({
      next: (data: any) => {
        this.requestData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.requestData || {});
        this.ensurePdfDoc();
      },
      error: (err) => console.error('Error loading material request print', err)
    });
  }

  private buildPreviewData(source: any) {
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const warehouse = this.pickEntity(source, ['warehouse', 'whmaster', 'branchwarehouse']);
    const items = this.extractItems(source).map((item: any, index: number) => {
      const history = this.extractIssueHistory(item);
      return {
        sr: index + 1,
        productName: this.safeValue(item.productname || item.itemname || item.description),
        uom: this.safeValue(item.uomname || item.uom),
        availableQty: this.formatNumber(item.availableqty || item.availablequantity || 0),
        requestedQty: this.formatNumber(item.requiredquantity || item.requestedqty || 0),
        requiredOn: this.formatDateValue(item.requiredon),
        specification: this.safeValue(item.specification, ''),
        remarks: this.safeValue(item.remarks, ''),
        issuedQty: this.formatNumber(item.receivedquantity || item.issuedquantity || 0),
        pendingQty: this.formatNumber(
          Math.max(
            Number(item.requiredquantity || item.requestedqty || 0) - Number(item.receivedquantity || item.issuedquantity || 0),
            0
          )
        ),
        issueHistory: history
      };
    });

    const summaryHistory = this.extractIssueHistory(source);
    const totalRequested = items.reduce((sum: number, item: any) => sum + Number(item.requestedQty.replace(/,/g, '')), 0);
    const totalIssued = items.reduce((sum: number, item: any) => sum + Number(item.issuedQty.replace(/,/g, '')), 0);

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      requestCode: this.safeValue(source.code),
      requestDate: this.formatDateValue(source.date || source.requestdate || source.createddate),
      status: this.safeValue(source.docstatusname || source.statusname || source.status, ''),
      version: this.safeValue(source.version, ''),
      warehouseName: this.safeValue(warehouse.name || warehouse.whname || source.whname || source.warehousename),
      warehouseAddress: this.firstMeaningfulText([
        warehouse.full_address,
        warehouse.fulladdress,
        this.joinAddress([warehouse.address1, warehouse.address2, warehouse.city, warehouse.state, warehouse.country, warehouse.pincode])
      ]),
      requestRemarks: this.safeValue(source.remarks, ''),
      items,
      totalItems: items.length,
      totalRequested: this.formatNumber(totalRequested),
      totalIssued: this.formatNumber(totalIssued),
      totalPending: this.formatNumber(Math.max(totalRequested - totalIssued, 0)),
      issueHistorySummary: summaryHistory
    };
  }

  private ensurePdfDoc() {
    if (!this.previewData) {
      return;
    }

    const body = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'ITEM', style: 'tableHeader' },
        { text: 'UOM', style: 'tableHeader' },
        { text: 'AVAILABLE', style: 'tableHeader' },
        { text: 'REQUESTED', style: 'tableHeader' },
        { text: 'ISSUED', style: 'tableHeader' },
        { text: 'PENDING', style: 'tableHeader' },
        { text: 'REQUIRED ON', style: 'tableHeader' },
        { text: 'ISSUE TRACE', style: 'tableHeader' }
      ],
      ...this.previewData.items.map((item: any) => ([
        { text: String(item.sr), alignment: 'center' },
        { text: item.productName },
        { text: item.uom, alignment: 'center' },
        { text: item.availableQty, alignment: 'right' },
        { text: item.requestedQty, alignment: 'right' },
        { text: item.issuedQty, alignment: 'right' },
        { text: item.pendingQty, alignment: 'right', bold: true },
        { text: item.requiredOn, alignment: 'center' },
        { text: item.issueHistory.length ? item.issueHistory.map((x: any) => `${x.code} ${x.date} Qty ${x.qty}`).join('\n') : 'Not issued yet', fontSize: 8 }
      ]))
    ];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 10, 20, 44],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              {
                width: '*',
                text: 'Generated using VCS ERP',
                style: 'footerText'
              },
              {
                width: 90,
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerPageText',
                alignment: 'right'
              }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? {
            image: this.previewData.logo,
            alignment: 'center',
            width: 280,
            height: 280,
            opacity: 0.05,
            margin: [0, 180, 0, 0]
          }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [150, '*', 150],
            body: [[
              this.previewData.logo
                ? {
                    stack: [
                      {
                        image: this.previewData.logo,
                        fit: [74, 74],
                        alignment: 'center',
                        margin: [0, 4, 0, 0]
                      }
                    ],
                    border: [false, false, false, false]
                  }
                : {
                    text: this.previewData.companyInitials || 'MR',
                    style: 'logoFallback',
                    border: [false, false, false, false]
                  },
              {
                stack: [
                  { text: this.previewData.companyName, style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((meta: string) => ({ text: meta, style: 'companyMeta' }))
                ].filter(Boolean),
                margin: [0, 3, 0, 0],
                border: [false, false, false, false]
              },
              {
                stack: [
                  { text: 'MATERIAL REQUEST', style: 'badge' },
                  { text: `Request Code: ${this.previewData.requestCode}`, style: 'meta' },
                  { text: `Request Date: ${this.previewData.requestDate}`, style: 'meta' },
                  { text: `Status: ${this.previewData.status || '-'}`, style: 'meta' },
                  this.previewData.version !== '-' ? { text: `Version: ${this.previewData.version}`, style: 'meta' } : ''
                ].filter(Boolean),
                fillColor: '#eef6ff',
                margin: [2, 6, 0, 0],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: [
            { width: '*', text: `Warehouse: ${this.previewData.warehouseName}`, style: 'summaryBox' },
            { width: '*', text: `Items: ${this.previewData.totalItems}`, style: 'summaryBox' },
            { width: '*', text: `Requested Qty: ${this.previewData.totalRequested}`, style: 'summaryBox' },
            { width: '*', text: `Pending Qty: ${this.previewData.totalPending}`, style: 'summaryBox' }
          ],
          columnGap: 8,
          margin: [0, 12, 0, 10]
        },
        {
          table: {
            headerRows: 1,
            widths: [22, '*', 34, 40, 42, 34, 38, 44, 96],
            body
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#16323f' : rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff',
            hLineColor: () => '#a7b5c8',
            vLineColor: () => '#a7b5c8'
          }
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Warehouse / Delivery Point', style: 'sectionHeader' },
                { text: this.previewData.warehouseName, style: 'detailText' },
                this.previewData.warehouseAddress ? { text: this.previewData.warehouseAddress, style: 'detailText' } : '',
                this.previewData.requestRemarks !== '-' ? { text: `Remarks: ${this.previewData.requestRemarks}`, style: 'detailText', margin: [0, 10, 0, 0] } : ''
              ].filter(Boolean)
            },
            {
              width: 200,
              stack: [
                { text: `For ${this.previewData.companyName}`, style: 'signatoryCompanyInline', alignment: 'right' },
                this.includeSignature && (this.previewData.stampImage || this.previewData.signatureImage)
                  ? {
                      columns: [
                        {
                          width: 72,
                          stack: [
                            this.previewData.stampImage
                              ? { image: this.previewData.stampImage, fit: [66, 66], opacity: 0.72, alignment: 'right', margin: [0, 4, -8, 0] }
                              : { text: '' }
                          ]
                        },
                        {
                          width: 118,
                          stack: [
                            this.previewData.signatureImage
                              ? { image: this.previewData.signatureImage, fit: [112, 38], alignment: 'right', margin: [0, 8, 0, 0] }
                              : { text: '' }
                          ]
                        }
                      ],
                      columnGap: -12,
                      margin: [0, 4, 0, 0]
                    }
                  : { text: '', margin: [0, 34, 0, 0] },
                this.previewData.signatoryName
                  ? { text: this.previewData.signatoryName, style: 'signatoryName', alignment: 'right', margin: [0, 4, 0, 0] }
                  : { text: '', margin: [0, 10, 0, 0] },
                { text: this.previewData.signatoryTitle || 'Authorized Signatory', style: 'signatoryRole', alignment: 'right' }
              ]
            }
          ],
          columnGap: 16,
          margin: [0, 12, 0, 0]
        }
      ],
      styles: {
        logoFallback: { fontSize: 22, bold: true, color: '#11284c', alignment: 'center', margin: [0, 22, 0, 0] },
        companyHeader: { fontSize: 17.6, bold: true, color: '#11284c', alignment: 'center' },
        companyMeta: { fontSize: 8.7, color: '#334155', alignment: 'center', margin: [0, 1, 0, 0] },
        badge: { fontSize: 11.5, bold: true, color: '#11284c', margin: [10, 10, 10, 7], alignment: 'left' },
        meta: { fontSize: 8.4, color: '#334155', margin: [10, 0, 10, 3], alignment: 'left' },
        summaryBox: { fontSize: 10, bold: true, color: '#0f172a', fillColor: '#f8fafc', margin: [8, 10, 8, 10] },
        sectionHeader: { fontSize: 10, bold: true, color: '#0f766e', margin: [0, 0, 0, 4] },
        detailText: { fontSize: 9, color: '#334155' },
        tableHeader: { fontSize: 8, bold: true, color: '#ffffff', alignment: 'center' },
        signatoryCompanyInline: { fontSize: 8.6, bold: true, color: '#334155' },
        signatoryName: { fontSize: 9.2, bold: true, color: '#11284c' },
        signatoryRole: { fontSize: 8.7, color: '#475569', bold: true },
        footerText: { fontSize: 6.8, color: '#64748b', margin: [0, 0, 0, 0] },
        footerPageText: { fontSize: 6.8, color: '#64748b' }
      },
      defaultStyle: { fontSize: 9, color: '#0f172a' }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private extractItems(source: any): any[] {
    const candidates = [
      source?.materialrequestdetails,
      source?.materialRequestDetails,
      source?.items,
      source?.details
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private extractIssueHistory(source: any): any[] {
    const candidates = [
      source?.issuehistory,
      source?.issueHistory,
      source?.materialissuehistory,
      source?.materialIssueHistory,
      source?.issuedocuments,
      source?.issueitems
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate.map((entry: any) => ({
          code: this.safeValue(entry.materialissuecode || entry.code || entry.issueslipno),
          date: this.formatDateValue(entry.issuedt || entry.date || entry.issueslipdt),
          qty: this.formatNumber(entry.issuedquantity || entry.qty || 0),
          remarks: this.safeValue(entry.remarks, '')
        }));
      }
    }

    return [];
  }

  private pickEntity(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value && typeof value === 'object') {
        return Array.isArray(value) ? value[0] || {} : value;
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    return [
      this.metaLine('GSTIN', company.gstin),
      this.metaLine('PAN', company.pan),
      this.metaLine('Email', company.email),
      this.metaLine('Website', company.website),
      this.metaLine('Phone', company.mobile || company.phone)
    ].filter(Boolean) as string[];
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      const normalized = this.normalizeImageCandidate(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeImageCandidate(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const image = value.trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image')) {
      return image;
    }

    if (/^[A-Za-z0-9+/=\r\n]+$/.test(image) && image.length > 120) {
      return `data:image/png;base64,${image.replace(/\s+/g, '')}`;
    }

    return null;
  }

  private metaLine(label: string, value: any): string {
    const normalized = this.normalizePart(value);
    return normalized ? `${label}: ${normalized}` : '';
  }

  private firstMeaningfulText(candidates: any[]): string {
    for (const candidate of candidates) {
      const normalized = this.normalizePart(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return '';
  }

  private joinAddress(parts: any[]): string {
    return parts.map((part) => this.normalizePart(part)).filter(Boolean).join(', ');
  }

  private normalizePart(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    return text === '-' ? '' : text;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  }

  private formatNumber(value: any): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value);
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private getInitials(value: any): string {
    const text = this.safeValue(value, 'MR');
    return text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }
}
